/**
 * Push notification utilities for subscribing/unsubscribing to push notifications
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Get the current notification permission status
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', or 'default'
}

/**
 * Request notification permission from the user
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Notifications not supported');
  }
  
  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Get the VAPID public key from the server
 */
async function getVapidPublicKey() {
  const response = await fetch(`${API_URL}/api/push/vapid-public-key`, {
    credentials: 'include'
  });
  
  if (!response.ok) {
    throw new Error('Failed to get VAPID public key');
  }
  
  const data = await response.json();
  return data.publicKey;
}

/**
 * Convert a base64 string to Uint8Array (for VAPID key)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  // Request permission first
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }
  
  // Get service worker registration
  const registration = await navigator.serviceWorker.ready;
  
  // Get VAPID public key
  const vapidPublicKey = await getVapidPublicKey();
  
  // Subscribe to push
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });
  
  // Send subscription to backend
  const response = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(subscription.toJSON())
  });
  
  if (!response.ok) {
    throw new Error('Failed to save subscription');
  }
  
  return subscription;
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) {
    return;
  }
  
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    // Unsubscribe locally
    await subscription.unsubscribe();
    
    // Tell backend to remove subscription
    await fetch(`${API_URL}/api/push/unsubscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ endpoint: subscription.endpoint })
    });
  }
}

/**
 * Check if user is currently subscribed to push notifications
 */
export async function isSubscribedToPush() {
  if (!isPushSupported()) {
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (e) {
    return false;
  }
}

/**
 * Get push subscription status from backend
 */
export async function getPushStatus() {
  try {
    const response = await fetch(`${API_URL}/api/push/status`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      return { subscribed: false, subscriptionCount: 0 };
    }
    
    return await response.json();
  } catch (e) {
    return { subscribed: false, subscriptionCount: 0 };
  }
}
